/**
 * field-wrapper.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Canonical Label + Input Wrapper
 *
 * WHY THIS EXISTS:
 *   Before: every form duplicated the label+spacing+error pattern
 *   with inconsistent fonts, margins, and error icon sizes.
 *   After: one component enforces the DS contract everywhere.
 *
 * DESIGN CONTRACT:
 *   Label    → always ABOVE the input, 12px medium, #4B5563
 *   Spacing  → 6px (gap-1.5) between label and input
 *   Error    → below input, 12px, red-500, with AlertCircle icon
 *   Hint     → below input, 11px, #9CA3AF (no icon)
 *   Required → red asterisk after label text
 *
 * ANATOMY:
 *   ┌────────────────────────────────┐
 *   │ Label text [*]                 │  ← 12px medium gray
 *   │ ┌──────────────────────────┐   │
 *   │ │ Input / Select / Custom  │   │  ← 40px, 8px radius
 *   │ └──────────────────────────┘   │
 *   │ ⚠ Error message                │  ← 12px red (conditional)
 *   │   Hint text                    │  ← 11px gray (conditional)
 *   └────────────────────────────────┘
 *
 * USAGE — basic:
 *   <FieldWrapper label="First Name" required>
 *     <Input placeholder="Anand Sharma" />
 *   </FieldWrapper>
 *
 * USAGE — with error:
 *   <FieldWrapper label="Email" required error="Invalid email address">
 *     <Input type="email" aria-invalid="true" />
 *   </FieldWrapper>
 *
 * USAGE — with hint:
 *   <FieldWrapper label="Password" hint="At least 8 characters">
 *     <Input type="password" />
 *   </FieldWrapper>
 *
 * USAGE — full width select:
 *   <FieldWrapper label="Department" required>
 *     <Select>
 *       <SelectTrigger><SelectValue /></SelectTrigger>
 *       <SelectContent>...</SelectContent>
 *     </Select>
 *   </FieldWrapper>
 *
 * USAGE — no label (inline context):
 *   <FieldWrapper>
 *     <Input placeholder="Search…" hasSearchIcon />
 *   </FieldWrapper>
 */

import React from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "./utils";

export interface FieldWrapperProps {
  /** Label text placed above the input */
  label?: React.ReactNode;
  /**
   * HTML `for` attribute linking the label to its input.
   * Required when label is a string for proper a11y.
   */
  htmlFor?: string;
  /** Appends a required asterisk (*) to the label */
  required?: boolean;
  /** Error message shown below the input (triggers red styling) */
  error?: React.ReactNode;
  /** Subtle hint shown below the input when no error is present */
  hint?: React.ReactNode;
  /** Extra className for the outer wrapper div */
  className?: string;
  /** The input, select, textarea, or any custom control */
  children: React.ReactNode;
}

export function FieldWrapper({
  label,
  htmlFor,
  required = false,
  error,
  hint,
  className = "",
  children,
}: FieldWrapperProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>

      {/* ── Label ── */}
      {label !== undefined && (
        <label
          htmlFor={htmlFor}
          data-slot="field-label"
          className="text-[12px] font-medium text-[#4B5563] leading-none select-none flex items-center gap-0.5"
        >
          {label}
          {required && (
            <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
          )}
        </label>
      )}

      {/* ── Control slot ── */}
      <div data-slot="field-control" className="w-full">
        {children}
      </div>

      {/* ── Error message ── */}
      {error && (
        <div
          data-slot="field-error"
          role="alert"
          className="flex items-start gap-1.5 mt-0.5"
        >
          <AlertCircle
            size={12}
            className="text-red-500 shrink-0 mt-[1px]"
            aria-hidden="true"
          />
          <span className="text-[12px] text-red-500 leading-tight">{error}</span>
        </div>
      )}

      {/* ── Hint message (only when no error) ── */}
      {!error && hint && (
        <p
          data-slot="field-hint"
          className="text-[11px] text-[#9CA3AF] leading-tight mt-0.5"
        >
          {hint}
        </p>
      )}
    </div>
  );
}

// ─── FilterLabel — for filter bars (different treatment from form labels) ────
/**
 * FilterLabel
 * Use this ONLY for filter row group labels (above compact selects in toolbars).
 * These are UPPERCASE, 11px, uppercase tracking — visually different from form labels.
 * Form labels use <FieldWrapper label="..."> instead.
 *
 * @example
 * <div className="flex flex-col gap-1">
 *   <FilterLabel>Month</FilterLabel>
 *   <Select>...</Select>
 * </div>
 */
export function FilterLabel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="filter-label"
      className={cn(
        "text-[11px] font-semibold text-[#9CA3AF]",
        "uppercase tracking-wide leading-none px-0.5",
        className,
      )}
    >
      {children}
    </span>
  );
}
